import { MapDataItem } from '@bpd-library/types';
import {
    ExtendedApartmentBuilding,
    ExtendedConstructionNumber,
    ExtendedProjectPhase,
    ExtendedPropertyType,
    getApiObjectCoordinates,
} from '@bpd-library/utilities';

import { formatDetailPageUrl, formatProjectPhaseStatusAsync } from '../api-formatters';

const createConstructionNumberMapData = (
    constructionNumber: ExtendedConstructionNumber,
    options?: { showSold?: boolean },
): MapDataItem[] | undefined => {
    const { id } = constructionNumber;

    const coordinates = getApiObjectCoordinates(constructionNumber);
    if (!coordinates) return;

    const variant = getConstructionNumberVariant(constructionNumber, options);
    const url = formatDetailPageUrl({ constructionNumber });

    return [
        {
            coordinates,
            id,
            variant,
            url,
            objectType: 'constructionNumber',
        },
    ];
};

const createPropertyTypeMapData = (
    propertyType: ExtendedPropertyType,
): MapDataItem[] | undefined => {
    const { id } = propertyType;

    const coordinates = getApiObjectCoordinates(propertyType);
    if (!coordinates) return;

    const variant = getPropertyTypeVariant(propertyType);
    const url = formatDetailPageUrl({ propertyType });

    return [
        {
            coordinates,
            id,
            variant,
            url,
            objectType: 'propertyType',
        },
    ];
};

const createProjectPhaseMapData = async (
    projectPhase: ExtendedProjectPhase,
    options?: { alwaysShowSoldProjectPolygons?: boolean },
): Promise<MapDataItem[] | undefined> => {
    const { id } = projectPhase;

    const coordinates = getApiObjectCoordinates(projectPhase);

    if (!coordinates) return;

    const status = await formatProjectPhaseStatusAsync(projectPhase);

    const useSoldVariant = options?.alwaysShowSoldProjectPolygons && projectPhase.status === 'Sold';

    return [
        {
            coordinates,
            id,
            variant: useSoldVariant ? 'projectPhaseSold' : 'projectPhase',
            objectType: 'projectPhase',
        },
        {
            coordinates,
            id: ` ${id}`,
            variant: useSoldVariant ? 'projectPhaseSoldPointer' : 'projectPhasePointer',
            title: projectPhase.name,
            status: status?.label,
        },
    ];
};

const createApartmentBuildingMapData = (
    apartmentBuilding: ExtendedApartmentBuilding,
): MapDataItem[] | undefined => {
    const { id } = apartmentBuilding;

    const coordinates = getApiObjectCoordinates(apartmentBuilding);
    if (!coordinates) return;

    const variant = getApartmentBuildingVariant(apartmentBuilding);

    return [
        {
            id,
            coordinates,
            variant,
            objectType: 'apartmentBuilding',
        },
    ];
};

const getConstructionNumberVariant = (
    constructionNumber: ExtendedConstructionNumber,
    options?: { showSold?: boolean },
): string => {
    const { propertyType, projectPhase, status } = constructionNumber;

    if (status === 'Reserved') return 'reserved';
    if (status === 'AssigningOn') return 'inOption';

    if (!propertyType || !projectPhase) return '';

    if (constructionNumber.availabilityStatus === 'Sold' && projectPhase.status === 'Sold')
        return options?.showSold ? 'underConstruction' : 'hidden';

    if (projectPhase.status === 'InPreparation') return 'inPreparation';

    if (
        constructionNumber.projectPhase?.status === 'OnSale' ||
        constructionNumber.projectPhase?.status === 'ForRent'
    ) {
        switch (constructionNumber.availabilityStatus) {
            case 'Available':
                return 'available';
            case 'InOption':
                return 'inOption';
            case 'Sold':
                return 'sold';
        }
    }

    return 'available';
};

const getPropertyTypeVariant = (propertyType: ExtendedPropertyType): string => {
    switch (propertyType.status) {
        case 'InPreparation':
        case 'SaleSoon':
            return 'inPreparation';
        case 'SaleStarted':
        case 'ForRent':
        case 'AmountAvailable':
            return 'available';
        case 'AssigningOn':
        case 'InOption':
            return 'inOption';
        case 'Sold':
        case 'Rented':
            return 'sold';
        case 'UnderConstruction':
            return 'underConstruction';
    }

    return '';
};

const getApartmentBuildingVariant = (apartmentBuilding: ExtendedApartmentBuilding): string => {
    const { status } = apartmentBuilding;

    switch (status) {
        case 'InPreparation':
        case 'SaleSoon':
            return 'inPreparation';
        case 'Available':
        case 'SaleStarted':
        case 'AmountAvailable':
        case 'ForRent':
            return 'available';
        case 'InOption':
        case 'AssigningOn':
            return 'inOption';
        case 'Rented':
        case 'Sold':
            return 'sold';
        case 'UnderConstruction':
            return 'underConstruction';
    }

    return '';
};

export {
    createConstructionNumberMapData,
    createPropertyTypeMapData,
    createProjectPhaseMapData,
    createApartmentBuildingMapData,
    getConstructionNumberVariant,
};
